import { Button } from '@/components/ui/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { LeaveRequestRow } from '../components/LeaveRequestRow';
import { SirhPageHeader } from '../components/SirhPageHeader';
import { sirhService } from '../services/sirh.service';
import type { LeaveRequest } from '../types';

/**
 * Écran responsable — « Demandes à approuver ».
 *
 * Le périmètre n'est pas décidé ici : la liste est celle que le serveur renvoie
 * pour cet emploi de responsable, à cette date. Un responsable qui essaierait
 * d'ouvrir la file d'un autre reçoit un refus, pas une liste vide.
 *
 * Les boutons visibles sont ceux que la machine à états du serveur autorise :
 * l'écran n'a aucune règle de transition en dur.
 */
export function LeaveApprovalsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'pending' | 'followUp'>('pending');
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [pendingDecision, setPendingDecision] = useState<
    'approuve' | 'refuse' | null
  >(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [restoreForUuid, setRestoreForUuid] = useState<string | null>(null);
  const [sicknessNotifiedAt, setSicknessNotifiedAt] = useState('');
  const [evidenceReference, setEvidenceReference] = useState('');

  const { data: me } = useQuery({
    queryKey: ['sirh', 'me'],
    queryFn: () => sirhService.me(),
  });
  const managerEmploymentUuid = me?.ownEmploymentUuids?.[0];

  const { data: stateMachine, isLoading: isStateMachineLoading } = useQuery({
    queryKey: ['sirh', 'state-machine'],
    queryFn: () => sirhService.stateMachine(),
  });

  const { data: requests = [], isLoading: isRequestsLoading } = useQuery({
    queryKey: ['sirh', 'inbox', managerEmploymentUuid],
    queryFn: () => sirhService.managerInbox(managerEmploymentUuid as string),
    enabled: Boolean(managerEmploymentUuid),
  });

  const decide = useMutation({
    mutationFn: (params: {
      uuid: string;
      expectedVersion: number;
      decision: 'approuve' | 'refuse';
      comment?: string;
    }) =>
      sirhService.decideLeave(params.uuid, {
        expectedVersion: params.expectedVersion,
        decision: params.decision,
        deciderEmploymentUuid: managerEmploymentUuid as string,
        comment: params.comment,
      }),
    onSuccess: () => {
      setPendingDecision(null);
      setDecisionComment('');
      void queryClient.invalidateQueries({ queryKey: ['sirh', 'inbox'] });
    },
  });

  const restoreOverlap = useMutation({
    mutationFn: (params: {
      uuid: string;
      notifiedAt: string;
      evidenceReference: string;
    }) =>
      sirhService.restorePaidLeaveOverlap(params.uuid, {
        notifiedAt: new Date(params.notifiedAt).toISOString(),
        evidenceReference: params.evidenceReference,
      }),
    onSuccess: () => {
      setRestoreForUuid(null);
      setSicknessNotifiedAt('');
      setEvidenceReference('');
      void queryClient.invalidateQueries({ queryKey: ['sirh', 'inbox'] });
    },
  });

  /** Une commande est proposée si, et seulement si, le serveur la déclare permise. */
  const canDecide = (request: LeaveRequest): boolean =>
    Boolean(
      stateMachine?.transitions.some(
        transition =>
          transition.from === request.state &&
          (transition.command === 'approve_final' ||
            transition.command === 'approve_level')
      )
    );

  const pendingRequests = useMemo(
    () =>
      requests.filter(request =>
        Boolean(
          stateMachine?.transitions.some(
            transition =>
              transition.from === request.state &&
              (transition.command === 'approve_final' ||
                transition.command === 'approve_level')
          )
        )
      ),
    [requests, stateMachine]
  );

  const followUpRequests = useMemo(
    () => requests.filter(request => !pendingRequests.includes(request)),
    [pendingRequests, requests]
  );

  const visible = useMemo(
    () =>
      (view === 'pending' ? pendingRequests : followUpRequests).filter(request =>
        search.trim().length === 0
          ? true
          : `${request.startsOn} ${request.endsOn} ${request.personLabel ?? ''}`
              .toLowerCase()
              .includes(search.trim().toLowerCase())
      ),
    [followUpRequests, pendingRequests, search, view]
  );

  const waiting = pendingRequests.length;
  const isLoading = isStateMachineLoading || isRequestsLoading;
  const selected =
    requests.find(request => request.uuid === selectedUuid) ?? null;

  const ouvrirDecision = (
    request: LeaveRequest,
    decision: 'approuve' | 'refuse'
  ) => {
    setSelectedUuid(request.uuid);
    setPendingDecision(decision);
    setDecisionComment('');
    setRestoreForUuid(null);
  };

  const confirmerDecision = () => {
    if (!selected || !pendingDecision) return;
    if (pendingDecision === 'refuse' && !decisionComment.trim()) return;
    decide.mutate({
      uuid: selected.uuid,
      expectedVersion: selected.version,
      decision: pendingDecision,
      comment: decisionComment.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <SirhPageHeader
        title="Demandes à approuver"
        subtitle={
          waiting > 0
            ? `${waiting} demande${waiting > 1 ? 's' : ''} attend${waiting > 1 ? 'ent' : ''} votre décision.`
            : 'Votre équipe n’a aucune demande en attente.'
        }
        actions={
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Rechercher une période"
              className="h-10 w-64 rounded-xl border border-neutral-300 pr-3 pl-9 text-sm placeholder:text-neutral-400/50"
            />
          </div>
        }
      />

      <div className="flex flex-wrap gap-2" aria-label="Type de demandes">
        <button
          type="button"
          onClick={() => setView('pending')}
          className={
            view === 'pending'
              ? 'rounded-full bg-neutral-950 px-4 py-2 text-sm font-medium text-white'
              : 'rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200'
          }
        >
          À décider{waiting > 0 ? ` (${waiting})` : ''}
        </button>
        <button
          type="button"
          onClick={() => setView('followUp')}
          className={
            view === 'followUp'
              ? 'rounded-full bg-neutral-950 px-4 py-2 text-sm font-medium text-white'
              : 'rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200'
          }
        >
          Suivi et restitutions
        </button>
      </div>

      <section className="space-y-3 rounded-xl border border-neutral-200 bg-white p-3">
        {isLoading && (
          <p className="px-4 py-8 text-sm text-neutral-500">Chargement…</p>
        )}
        {!isLoading && visible.length === 0 && (
          <p className="px-4 py-8 text-sm text-neutral-500">
            {search.trim()
              ? 'Aucune demande ne correspond à votre recherche.'
              : view === 'pending'
                ? 'Votre équipe n’a aucune demande en attente.'
                : 'Aucune demande traitée ne nécessite de suivi.'}
          </p>
        )}
        {visible.map(request => (
          <LeaveRequestRow
            key={request.uuid}
            request={request}
            personLabel={request.personLabel}
            selected={request.uuid === selectedUuid}
            onSelect={() => {
              setSelectedUuid(request.uuid);
              setPendingDecision(null);
              setDecisionComment('');
              setRestoreForUuid(null);
              setSicknessNotifiedAt('');
              setEvidenceReference('');
            }}
            actions={
              canDecide(request) ? (
                <>
                  <Button
                    size="sm"
                    variant="success"
                    className="h-8 w-8 p-0"
                    title="Approuver"
                    aria-label={`Approuver la demande de ${request.personLabel ?? 'ce salarié'}`}
                    disabled={decide.isPending}
                    onClick={() => ouvrirDecision(request, 'approuve')}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    title="Refuser"
                    aria-label={`Refuser la demande de ${request.personLabel ?? 'ce salarié'}`}
                    disabled={decide.isPending}
                    onClick={() => ouvrirDecision(request, 'refuse')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : undefined
            }
          />
        ))}
      </section>

      {selected && (
        <section className="max-w-2xl rounded-xl border border-neutral-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-neutral-900">
                {selected.personLabel ?? 'Demande sélectionnée'}
              </p>
              <p className="mt-1 text-sm text-neutral-600">
                Du {selected.startsOn} au {selected.endsOn} ·{' '}
                {Number(selected.countedDays).toLocaleString('fr-FR')} jour
                {Number(selected.countedDays) > 1 ? 's' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedUuid(null);
                setPendingDecision(null);
                setDecisionComment('');
                setRestoreForUuid(null);
                setSicknessNotifiedAt('');
                setEvidenceReference('');
              }}
              className="min-h-10 rounded-lg px-3 text-sm text-neutral-600 hover:bg-neutral-100"
            >
              Fermer
            </button>
          </div>

          {selected.employeeComment && (
            <div className="mt-4 border-l-2 border-neutral-200 pl-3">
              <p className="text-xs font-medium text-neutral-500">
                Commentaire du salarié
              </p>
              <p className="mt-1 text-sm whitespace-pre-wrap text-neutral-700">
                {selected.employeeComment}
              </p>
            </div>
          )}

          {selected.justificationReference && (
            <div className="mt-4 border-l-2 border-neutral-200 pl-3">
              <p className="text-xs font-medium text-neutral-500">
                Référence du justificatif
              </p>
              <p className="mt-1 text-sm text-neutral-700">
                {selected.justificationReference}
              </p>
            </div>
          )}

          {selected.overlapRestoredAt && (
            <p className="text-success-700 mt-4 text-sm">
              Les jours de congés payés chevauchants ont été restitués sur
              preuve de notification.
            </p>
          )}

          {selected.canRestorePaidLeaveOverlap &&
            restoreForUuid !== selected.uuid && (
              <button
                type="button"
                onClick={() => {
                  setRestoreForUuid(selected.uuid);
                  setPendingDecision(null);
                }}
                className="mt-5 min-h-10 rounded-lg px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-neutral-950"
              >
                Restituer les congés chevauchants
              </button>
            )}

          {selected.canRestorePaidLeaveOverlap &&
            restoreForUuid === selected.uuid && (
              <div className="mt-5 border-t border-neutral-100 pt-5">
                <p className="text-sm font-medium text-neutral-900">
                  Preuve de notification de l’arrêt maladie
                </p>
                <p className="mt-1 text-sm text-neutral-600">
                  La restitution reste volontaire : elle n’est enregistrée
                  qu’avec une date certaine et une référence de preuve.
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm text-neutral-600">
                      Notification reçue le
                    </span>
                    <input
                      type="datetime-local"
                      value={sicknessNotifiedAt}
                      onChange={event =>
                        setSicknessNotifiedAt(event.target.value)
                      }
                      className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-neutral-600">
                      Référence de la preuve
                    </span>
                    <input
                      value={evidenceReference}
                      maxLength={240}
                      onChange={event =>
                        setEvidenceReference(event.target.value)
                      }
                      placeholder="Identifiant du document ou de l’accusé"
                      className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm placeholder:text-neutral-400/50"
                    />
                  </label>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    size="sm"
                    variant="success"
                    disabled={
                      restoreOverlap.isPending ||
                      !sicknessNotifiedAt ||
                      !evidenceReference.trim()
                    }
                    onClick={() =>
                      restoreOverlap.mutate({
                        uuid: selected.uuid,
                        notifiedAt: sicknessNotifiedAt,
                        evidenceReference: evidenceReference.trim(),
                      })
                    }
                  >
                    {restoreOverlap.isPending
                      ? 'Restitution…'
                      : 'Confirmer la restitution'}
                  </Button>
                  <button
                    type="button"
                    disabled={restoreOverlap.isPending}
                    onClick={() => {
                      setRestoreForUuid(null);
                      setSicknessNotifiedAt('');
                      setEvidenceReference('');
                    }}
                    className="min-h-9 rounded-lg px-3 text-sm text-neutral-600 hover:bg-neutral-100"
                  >
                    Retour
                  </button>
                </div>
                {restoreOverlap.isError && (
                  <p className="text-error-700 mt-3 text-sm" role="alert">
                    La restitution n’a pas pu être enregistrée. Vérifiez la
                    preuve et le chevauchement, puis réessayez.
                  </p>
                )}
              </div>
            )}

          {pendingDecision && canDecide(selected) && (
            <div className="mt-5 border-t border-neutral-100 pt-5">
              <p className="text-sm font-medium text-neutral-900">
                {pendingDecision === 'approuve'
                  ? 'Confirmer l’approbation'
                  : 'Expliquer puis confirmer le refus'}
              </p>
              <label className="mt-3 block">
                <span className="text-sm text-neutral-600">
                  {pendingDecision === 'refuse'
                    ? 'Motif du refus'
                    : 'Commentaire facultatif'}
                </span>
                <textarea
                  rows={3}
                  maxLength={2_000}
                  value={decisionComment}
                  onChange={event => setDecisionComment(event.target.value)}
                  placeholder={
                    pendingDecision === 'refuse'
                      ? 'Indiquez au salarié ce qui motive la décision'
                      : 'Ajoutez seulement une précision utile'
                  }
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm placeholder:text-neutral-400/50"
                />
              </label>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  size="sm"
                  variant={
                    pendingDecision === 'approuve' ? 'success' : 'outline'
                  }
                  disabled={
                    decide.isPending ||
                    (pendingDecision === 'refuse' && !decisionComment.trim())
                  }
                  onClick={confirmerDecision}
                >
                  {decide.isPending
                    ? 'Enregistrement…'
                    : pendingDecision === 'approuve'
                      ? 'Confirmer l’approbation'
                      : 'Confirmer le refus'}
                </Button>
                <button
                  type="button"
                  disabled={decide.isPending}
                  onClick={() => {
                    setPendingDecision(null);
                    setDecisionComment('');
                  }}
                  className="min-h-9 rounded-lg px-3 text-sm text-neutral-600 hover:bg-neutral-100"
                >
                  Retour
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {decide.isError && (
        <p className="border-error-200 bg-error-50 text-error-700 rounded-xl border px-4 py-3 text-sm">
          La demande a changé depuis son affichage. Rechargez la liste avant de
          décider — c’est ce qui garantit qu’une double approbation ne compte
          pas deux fois.
        </p>
      )}
    </div>
  );
}
